<script lang="ts">
    import { getContext, onDestroy, onMount } from "svelte";
    import type { Writable } from "svelte/store";

    import type { SkinState, State } from "../types/state";
    let state: Writable<State> = getContext("playerState");
    let skinState: Writable<SkinState> = getContext("skinState");

    import ControlBar from "./controlBar/ControlBar.svelte";
    import ClipControls from "./controlBar/ClipControls.svelte";
    import Slider from "./controlBar/Slider.svelte";
    import Overlay from "./overlays/Overlay.svelte";
    import InfoBox from "./overlays/infoBox/InfoBox.svelte";
    import LoaderOverlay from "./overlays/LoaderOverlay.svelte";
    import SettingsOverlay from "./overlays/settingsOverlay/SettingsOverlay.svelte";
    import { watcher } from "../utils/watcher";
    import {
        toggleFullscreen,
        isFullscreen,
    } from "../../../../modules/vu-core/dom/fullscreen";
    import { togglePictureInPicture } from "../../../../modules/vu-core/dom/pip";

    let activeDuration = 2500;

    let isInteracting = watcher(false, onControlsChange);
    let isSettingsOpen = watcher(false, onControlsChange);
    function onEndInteraction() {
        $isInteracting = false;
    }

    function onStartInteraction(evt) {
        const skipIfFullscreen =
            evt.type === "mouseenter" && $skinState.isFullscreen;
        if (skipIfFullscreen) {
            return;
        }

        $isInteracting = true;
    }

    let hideControlsTimeout;

    function hideWithDelay(delay = activeDuration) {
        clearTimeout(hideControlsTimeout);

        hideControlsTimeout = setTimeout(() => {
            $skinState.isOverlayHidden = true;
        }, delay);
    }

    $: isPaused = $state.playerState !== "playing";
    $: {
        isPaused; // It is here to tell svelte the relation with the watcher
        onControlsChange();
    }

    function onControlsChange() {
        clearTimeout(hideControlsTimeout);

        if ($isInteracting || $isSettingsOpen) {
            $skinState.isOverlayHidden = false;
            return;
        }

        if (!isPaused) {
            $skinState.isOverlayHidden = false;
            hideWithDelay();
            return;
        }
        $skinState.isOverlayHidden = false;
    }

    const fullScreenEvents = [
        "webkitfullscreenchange",
        "mozfullscreenchange",
        "fullscreenchange",
        "MSFullscreenChange",
        "webkitbeginfullscreen",
        "webkitendfullscreen",
    ];
    onMount(() => {
        fullScreenEvents.forEach((event) => {
            $state.playerContainer.addEventListener(event, onFullscreenchange);
        });
    });
    onDestroy(() => {
        fullScreenEvents.forEach((event) => {
            $state.playerContainer.removeEventListener(
                event,
                onFullscreenchange
            );
        });
    });
    function onFullscreenchange() {
        $skinState.isFullscreen = isFullscreen();
    }

    function togglePlayPause() {
        $state.adapter?.isPaused
            ? $state.adapter?.play()
            : $state.adapter?.pause();
    }

    function closeSettings() {
        if (!$skinState.isSettingsOpen || isFullscreen()) {
            return;
        }
        $skinState.isSettingsOpen = false;
        $skinState.setFocusOnSettingsButton();
    }

    function toggleMute() {
        $state.adapter?.isMuted
            ? $state.adapter?.unmute()
            : $state.adapter?.mute();
    }

    function keyup(event: KeyboardEvent) {
        switch (event.key) {
            case "Esc": // IE/Edge specific value
            case "Escape":
                closeSettings();
                break;
            case "k":
                togglePlayPause();
                break;
            case "f":
                toggleFullscreen($state.playerContainer);
                break;
            case "m":
                toggleMute();
                break;
            case "j":
                $state.adapter?.skipTo(-10);
                break;
            case "l":
                $state.adapter?.skipTo(10);
                break;
            case "i":
                togglePictureInPicture($state.adapter?.video);
                break;
            default:
                break;
        }
    }

    function windowKeydown(event: KeyboardEvent) {
        switch (event.key) {
            case "Shift":
                $skinState.isShiftKeyPressed = true;
                break;
            default:
                break;
        }
    }

    function windowKeyup(event: KeyboardEvent) {
        switch (event.key) {
            case "Shift":
                $skinState.isShiftKeyPressed = false;
                break;
            default:
                break;
        }
    }
</script>

<svelte:window on:keydown={windowKeydown} on:keyup={windowKeyup} />

<div
    bind:this={$state.playerContainer}
    tabindex="0"
    class="scotparl-player"
    on:mouseenter={onStartInteraction}
    on:mouseleave={onEndInteraction}
    on:mousemove={onEndInteraction}
    on:touchstart={onStartInteraction}
    on:touchend={onEndInteraction}
    on:focus={onStartInteraction}
    on:blur={onEndInteraction}
    on:keyup={keyup}
    bind:clientWidth={$skinState.playerWidth}
    bind:clientHeight={$skinState.playerHeight}
>
    <slot />

    <div class="skin-container">
        <Overlay />
        <InfoBox />
        <LoaderOverlay />
        <ClipControls />
        <SettingsOverlay />
        <Slider />
        <ControlBar />
    </div>
</div>

<style type="text/scss" lang="scss">
    @import "../globals/mixins.scss";
    .scotparl-player {
        @include defaultText();

        *,
        *:before,
        *:after {
            box-sizing: border-box;
        }

        display: block;
        position: relative;
        direction: ltr;

        width: 100%;
        height: 100%;
        max-height: 100%;

        min-width: 250px;
        min-height: 100px;

        margin: auto;

        background-color: #000;
    }

    :global(::cue) {
        line-height: 1.2em;
    }
</style>
